import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { NgbActiveModal, NgbDate } from '@ng-bootstrap/ng-bootstrap';

import { DurationService } from 'src/app/services/duration.service';
import { Licence, Role, Template, Profile, TemplateProfile, Instant } from 'src/app/services/jsonrpc/user-server.service';
import { UserService } from 'src/app/services/user.service';
import { environment } from 'src/environments/environment';

export enum Feature {
  optional,
  required
}

export interface UserModalConfig {
  header: string; // modal type for the title
  update: boolean; // create or update?
  state?: boolean; // toggle user enabled/disabled state
  settings: boolean; // show/hide settings
  template: boolean; // user or template
  pattern: boolean; // only for template
  licence?: Feature; // only for template
}

export interface Model extends Template, Licence {
  profile: Profile & TemplateProfile;
}

export interface UserModalUpdate {
  model: Model;
  password?: string;
  activateLicences: string[];
}

@Component({
  selector: 'app-user-modal',
  templateUrl: './user-modal.component.html',
  styleUrls: ['./user-modal.component.scss']
})
export class UserModalComponent implements OnInit {
  public Feature = Feature;

  @Input() public config: UserModalConfig;
  @Input() public roles: Role[] = [];
  @Input() public model: Model = { profile: { username: '', meta: {}, settings: {}, labels: {}, enabled: true }, precedence: 0 };

  @Output() public onSubmit = new EventEmitter<UserModalUpdate>();

  public readonly licenceUsageInfo = UserService.licenceUsageInfo;
  public readonly dateFormat = environment.dateFormat;
  public readonly developer = environment.developer;

  public username: string;
  public expiration: NgbDate;
  public password = '';
  public passwordConfirm = '';
  public activate = {};

  public get title(): string {
    const action = this.config.update ? 'Update' : 'Create';
    return `${action} ${this.config.header}`;
  }

  public get passwordRequired(): boolean {
    // password is required for user creation
    return !this.config.template && !this.config.update;
  }

  public licenceEditing: boolean;
  public passwordEditing: boolean;
  public editCustomFields: boolean;

  private allowEmptyRoles: boolean;

  constructor(public duration: DurationService, public modal: NgbActiveModal) {}

  ngOnInit(): void {
    this.username = this.defaultUsernameOrPattern();
    this.expiration = this.instantToNgbDate(this.model.profile.meta.expiration);
    this.licenceEditing = this.config.licence === Feature.required || (this.model.key != null && this.model.key !== '');
    this.passwordEditing = this.passwordRequired;
    this.allowEmptyRoles =
      this.config.update && (this.model.profile.meta.roles?.length === 0 || this.model.profile.meta.licences?.length > 0);
  }

  private defaultUsernameOrPattern(): string {
    const c = this.config;
    const m = this.model;
    if (!c.template) return m.profile.username;
    else if (!c.pattern) return m.username;
    else if (c.update) return m.pattern;
    else return '%'; // default for new templates
  }

  public isValid(): boolean {
    return (
      this.isValidUsername() &&
      this.isValidMail() &&
      this.isValidRoles() &&
      this.isValidLicence() &&
      this.isValidExpirationOrDuration() &&
      this.isValidDuration() &&
      this.isValidPassword() &&
      this.isValidPasswordConfirm() &&
      this.isValidVolume()
    );
  }

  public submit() {
    // update the correct username/pattern field
    if (this.config.template && this.config.pattern) {
      this.model.pattern = this.username; // update template pattern
      this.model.username = null;
      this.model.profile.username = null;
    } else if (this.config.template) {
      this.model.pattern = null;
      this.model.username = this.username; // update template username
      this.model.profile.username = null;
    } else {
      this.model.pattern = null;
      this.model.username = null;
      this.model.profile.username = this.username; // update profile username
    }

    // map empty fields -> null
    if (this.model.profile.meta.mail === '') {
      this.model.profile.meta.mail = null;
    }
    if (this.model.profile.meta.duration === '') {
      this.model.profile.meta.duration = null;
    }

    // parse expiration
    this.model.profile.meta.expiration = this.ngbDateToInstant(this.expiration);

    // apply licence key condition
    if (this.config.template) {
      this.model.key = this.licenceEditing ? this.nullIfEmpty(this.model.key) : null;
    }

    // apply account confirmation type
    if (this.model.profile.meta.mail != null || this.model.pattern?.match(/^\S+@\S+\.\S+$/) != null) {
      this.model.profile.meta.confirmation = 'mail';
    } else {
      this.model.profile.meta.confirmation = 'none';
    }

    // calculate default template precedence
    if (this.config.template) {
      this.model.precedence =
        (this.model.key != null ? 4000 : 0) +
        (this.model.pattern == null ? 2000 : 0) +
        (this.model.profile.meta.confirmation === 'mail' ? 1000 : 0);
    }

    // clear unsupported options for empowerment licences
    if (this.model.usage === 'empowerment') {
      this.model.profile.labels = {};
      this.model.profile.settings = {};
      this.model.profile.meta.confirmation = null;
    }

    // clear password if disabled
    if (this.config.template || !this.passwordEditing) {
      this.password = null;
    }

    // licences to be updated
    const activateLicences = Object.keys(this.activate).filter(key => this.activate[key]);

    this.onSubmit.emit({ model: this.model, password: this.password, activateLicences });
    this.modal.close();
  }

  public isValidUsername(): boolean {
    return this.config.pattern
      ? this.isValidField(this.username, /^[\wÄÖÜäöüß0-9@%\.\-]{1,200}$/, true)
      : this.isValidField(this.username, /^[\wÄÖÜäöüß0-9@\.\-]{3,200}$/, true);
  }

  public isRegularUsername(): boolean {
    return this.isValidUsername() && !(this.username.includes('%') || this.username.includes('_'));
  }

  public isTemplateMail(): boolean {
    return this.isValidField(this.username, /^\S+@\S+\.\S+$/, false);
  }

  public isValidMail(): boolean {
    return this.isValidField(this.model.profile.meta.mail, /^\S+@\S+\.\S+$/, false);
  }

  public isValidExpiration(): boolean {
    return this.expiration == null || this.expiration.day > 0;
  }

  public isValidDuration(): boolean {
    return (
      !this.config.template ||
      this.isValidField(
        this.model.profile.meta.duration,
        /^P(?!$)(\d+(\.\d+)?Y)?(\d+(\.\d+)?M)?(\d+(\.\d+)?W)?(\d+(\.\d+)?D)?(T(?=\d)(\d+(\.\d+)?H)?(\d+(\.\d+)?M)?(\d+(\.\d+)?S)?)?$/,
        false
      )
    );
  }

  public isValidExpirationOrDuration(): boolean {
    return this.expiration == null || this.model.profile.meta.duration == null;
  }

  public isValidRoles(): boolean {
    return this.roles.length == 0 || this.model.profile.meta.roles?.length > 0 || this.allowEmptyRoles;
  }

  public isValidLicence(): boolean {
    return (
      !this.config.template ||
      this.isValidField(this.model.key, /^[\w\.\-\/+=]{4,100}$/, this.config.licence === Feature.required || this.licenceEditing)
    );
  }

  public isValidPassword(): boolean {
    return this.config.template || !this.passwordEditing || this.password.length >= 4;
  }

  public isValidPasswordConfirm(): boolean {
    return this.config.template || !this.passwordEditing || this.password == this.passwordConfirm;
  }

  public isValidVolume(): boolean {
    return this.isValidField(this.model.volume?.toString(), /^\d*$/, false);
  }

  private isValidField(value: any, pattern: RegExp, required: boolean): boolean {
    return value == null || value === '' ? !required : value.match(pattern) != null;
  }

  public removeLicenceAt(index: number) {
    this.model.profile.meta.licences?.splice(index, 1);
  }

  private instantToNgbDate(i: Instant): NgbDate {
    if (i) {
      const d = new Date(i);
      return new NgbDate(d.getFullYear(), d.getMonth() + 1, d.getDate());
    } else {
      return null;
    }
  }

  private ngbDateToInstant(d: NgbDate): Instant {
    if (d) {
      return new Date(d.year, d.month - 1, d.day).toJSON();
    } else {
      return null;
    }
  }

  private nullIfEmpty(s?: string) {
    return s && s !== '' ? s : null;
  }
}
